import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ButtonComponent } from '@shared/components/button/button.component';
import {
  ReasonModalData,
  ReasonModalModalComponent,
  ReasonModalResponse,
} from '@shared/components/modals/reason-modal/reason-modal.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { PermissionType } from '@shared/services/gql.service';

import { InvoiceBannerButtonDirective } from './invoice-detail-banner-button.directive';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: InvoiceBannerButtonDirective,
      inputs: ['invoice'],
    },
  ],
  imports: [ButtonComponent, TooltipDirective],
  selector: 'aux-invoice-detail-status-banner-delete-button',
  template: `
    <aux-button
      [auxTooltip]="tooltip()"
      [disabled]="disabled()"
      [onClick]="onDelete"
      icon="Trash"
      variant="negative"
      label="Delete"
    />
  `,
})
export class InvoiceDetailDeleteBtnComponent {
  ctx = inject(InvoiceBannerButtonDirective);
  router = inject(Router);

  hasDeletePermission = this.ctx.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_DELETE_INVOICE],
  });

  disabled = computed(() => {
    return (
      !this.hasDeletePermission() ||
      this.ctx.buttonDisabled() ||
      this.ctx.isAccrualPeriodInClosedMonth()
    );
  });
  tooltip = computed(() => {
    if (!this.hasDeletePermission()) {
      return this.ctx.dontHavePermission;
    }

    if (this.ctx.buttonDisabled()) {
      return this.ctx.invoiceLockTooltip();
    }

    if (this.ctx.isAccrualPeriodInClosedMonth()) {
      return 'Invoice in closed month cannot be deleted.';
    }

    return '';
  });

  onDelete = async () => {
    const { invoiceQuery, invoicesPageService, overlayService, userTaskService } = this.ctx;
    const invoice = this.ctx.invoice();

    const resp = overlayService.openPopup<
      ReasonModalData,
      ReasonModalResponse,
      ReasonModalModalComponent
    >({
      content: ReasonModalModalComponent,
      data: {
        message: `Are you sure you want to remove Invoice ${invoice.invoice_no}?`,
      },
      settings: {
        header: 'Remove Invoice?',
        primaryButton: {
          action: (instance: null | ReasonModalModalComponent) => instance?.save(),
          label: 'Remove',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      const id = invoiceQuery.getEntity(invoice.id);

      if (!id) {
        return;
      }

      const { success } = await invoicesPageService.remove(id, event.data?.reason);

      if (success) {
        await userTaskService.triggerUserTaskList$();
        await this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`
        );
      }
    }
  };
}
